/**
 * 资源库页面 — 三栏布局，支持图片/PDF/Word
 * 左: 分类树 | 中: 文件网格 | 右: 详情预览面板
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { mediaApi, ImageCategory, ImageAsset, PaginatedImages } from '../services/mediaApi';
import { toast } from '../store/toastStore';
import ImagePreviewModal from '../components/ImagePreviewModal';
import PageHeader from '../components/layout/PageHeader';
import { stripExt } from '../utils/format';
import { ArrowPathIcon, ArrowUpTrayIcon, ClipboardDocumentListIcon, DocumentTextIcon, FolderIcon, TrashIcon } from '@heroicons/react/24/outline';

const PAGE_SIZE = 12;

const isImageFile = (asset: ImageAsset) =>
  asset.mime_type?.startsWith('image/') ||
  /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(asset.filename || asset.original_name);

const fileIcon = (asset: ImageAsset): string => {
  const mime = asset.mime_type || '';
  const name = asset.filename || asset.original_name || '';
  if (mime.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(name)) return '🖼️';
  if (mime.includes('pdf') || name.endsWith('.pdf')) return '📄';
  if (mime.includes('word') || mime.includes('doc') || /\.(doc|docx)$/i.test(name)) return '📝';
  return '📎';
};

const MediaLibrary: React.FC = () => {
  // ── 状态 ──
  const [categories, setCategories] = useState<ImageCategory[]>([]);
  const [activeCategoryId, setActiveCategoryId] = useState<number | 'all'>('all');
  const [images, setImages] = useState<ImageAsset[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const [selectedImage, setSelectedImage] = useState<ImageAsset | null>(null);
  const [detailImage, setDetailImage] = useState<ImageAsset | null>(null);

  // 上传模态框
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number[]>([]);
  const [uploadTargetCatId, setUploadTargetCatId] = useState<number | null>(null);
  const [uploadTargetCatName, setUploadTargetCatName] = useState('未分类');

  // 全屏预览
  const [fullscreen, setFullscreen] = useState<ImageAsset | null>(null);

  // 分类编辑
  const [newCatName, setNewCatName] = useState('');
  const [showNewCat, setShowNewCat] = useState(false);
  const [contextCat, setContextCat] = useState<ImageCategory | null>(null);
  const [ctxMenuPos, setCtxMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [renameCat, setRenameCat] = useState<ImageCategory | null>(null);
  const [renameName, setRenameName] = useState('');
  const [confirmDeleteCat, setConfirmDeleteCat] = useState<ImageCategory | null>(null);
  const [newSubCatParent, setNewSubCatParent] = useState<ImageCategory | null>(null);
  const [newSubCatName, setNewSubCatName] = useState('');

  // 详情编辑状态
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [editCatId, setEditCatId] = useState<number>(0);
  const [savedFlash, setSavedFlash] = useState(false);

  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  // ── 加载分类树 ──
  const loadCategories = useCallback(async () => {
    try {
      const res = await mediaApi.getCategoriesTree();
      setCategories(res.data);
    } catch (e) { console.error(e); }
  }, []);

  // ── 加载图片列表 ──
  const loadImages = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, page_size: PAGE_SIZE, sort_by: sortBy };
      if (activeCategoryId !== 'all') params.category_id = activeCategoryId;
      if (searchQ) params.q = searchQ;
      const res = await mediaApi.listImages(params);
      setImages(res.data.items);
      setTotal(res.data.total);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, activeCategoryId, searchQ, sortBy]);

  // ── 加载详情 ──
  const loadDetail = useCallback(async (id: number) => {
    try {
      const res = await mediaApi.getImage(id);
      setDetailImage(res.data);
      setEditName(res.data.original_name);
      setEditDesc(res.data.ai_description || '');
      setEditTags(res.data.ai_tags || []);
      setEditCatId(res.data.category_id);
    } catch (e) { console.error(e); }
  }, []);

  // ── 选中图片 ──
  const selectImage = (img: ImageAsset) => {
    setSelectedImage(img);
    loadDetail(img.id);
  };

  // ── 初始加载 ──
  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadImages(); }, [loadImages]);

  // ── 搜索防抖 ──
  const doSearch = (val: string) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setSearchQ(val); setPage(1); }, 300);
  };

  // ── 保存详情字段 ──
  const saveField = async (field: 'name' | 'desc' | 'tags' | 'cat', value: any) => {
    if (!detailImage) return;
    try {
      if (field === 'name') await mediaApi.updateImage(detailImage.id, { original_name: value });
      else if (field === 'desc') await mediaApi.updateImage(detailImage.id, { ai_description: value });
      else if (field === 'tags') await mediaApi.updateImage(detailImage.id, { ai_tags: value });
      else if (field === 'cat') await mediaApi.updateImage(detailImage.id, { category_id: value });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1000);
      loadImages();
      loadCategories();
      loadDetail(detailImage.id);
    } catch (e) { console.error(e); }
  };

  // ── 删除图片 ──
  const confirmDeleteImage = async (id: number) => {
    if (!window.confirm('确定要删除这张图片？此操作不可撤销。')) return;
    try {
      await mediaApi.deleteImage(id);
      setSelectedImage(null);
      setDetailImage(null);
      loadImages();
      loadCategories();
    } catch (e) { console.error(e); }
  };

  // ── 手动分析 ──
  const handleAnalyze = async (id: number) => {
    setAnalyzing(true);
    try {
      await mediaApi.analyzeImage(id);
      loadDetail(id);
      loadImages();
      loadCategories();
      toast.success('AI 分析完成');
    } catch (e) {
      toast.error('分析失败，请重试');
      console.error(e);
    } finally {
      setAnalyzing(false);
    }
  };

  // ── 上传处理 ──
  const handleUploadFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setUploadFiles(Array.from(e.target.files));
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files) setUploadFiles(Array.from(e.dataTransfer.files));
  };
  const removeUploadFile = (i: number) => setUploadFiles(prev => prev.filter((_, j) => j !== i));

  const doUpload = async () => {
    if (!uploadFiles.length) return;
    setUploading(true);
    setUploadProgress(uploadFiles.map(() => 0));
    try {
      await mediaApi.upload(uploadFiles, uploadTargetCatId || undefined, (pct) => {
        setUploadProgress(uploadFiles.map(() => pct));
      });
      setUploadOpen(false);
      setUploadFiles([]);
      setUploading(false);
      loadImages();
      loadCategories();
    } catch (e) {
      alert('上传失败');
      setUploading(false);
    }
  };

  // ── 分类操作 ──
  const createCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      await mediaApi.createCategory({ name: newCatName.trim() });
      setNewCatName('');
      setShowNewCat(false);
      loadCategories();
    } catch (e: any) { alert(e.response?.data?.detail || '创建失败'); }
  };
  const doRenameCat = async () => {
    if (!renameCat || !renameName.trim()) return;
    try {
      await mediaApi.updateCategory(renameCat.id, { name: renameName.trim() });
      setRenameCat(null);
      loadCategories();
    } catch (e: any) { alert(e.response?.data?.detail || '重命名失败'); }
  };
  const doDeleteCat = async (cat: ImageCategory) => {
    if (!window.confirm(`"${cat.name}" 下有 ${cat.image_count} 张图片，将移至「未分类」。确认删除？`)) return;
    try {
      await mediaApi.deleteCategory(cat.id);
      setConfirmDeleteCat(null);
      if (activeCategoryId === cat.id) setActiveCategoryId('all');
      loadCategories();
      loadImages();
    } catch (e: any) { alert(e.response?.data?.detail || '删除失败'); }
  };
  const createSubCategory = async () => {
    if (!newSubCatParent || !newSubCatName.trim()) return;
    try {
      await mediaApi.createCategory({ name: newSubCatName.trim(), parent_id: newSubCatParent.id });
      setNewSubCatParent(null);
      setNewSubCatName('');
      loadCategories();
      toast.success(`已在「${newSubCatParent.name}」下创建子分类「${newSubCatName.trim()}」`);
    } catch (e: any) { alert(e.response?.data?.detail || '创建子分类失败'); }
  };

  // ── 辅助 ──
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const getImageUrl = (img: ImageAsset) => {
    const base = process.env.REACT_APP_API_URL ?? '';
    return `${base}/${img.file_path}`;
  };
  const formatSize = (b: number) => b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`;
  const formatDate = (s: string) => {
    const d = new Date(s);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return '刚刚';
    if (mins < 60) return `${mins}分钟前`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}小时前`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}天前`;
    return d.toLocaleDateString('zh-CN');
  };

  // ── 所有分类扁平列表（用于下拉） ──
  const flatCats = (cats: ImageCategory[]): { id: number; name: string; level: number }[] => {
    const result: { id: number; name: string; level: number }[] = [];
    for (const c of cats) {
      result.push({ id: c.id, name: c.name, level: 0 });
      for (const ch of c.children) result.push({ id: ch.id, name: ch.name, level: 1 });
    }
    return result;
  };

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="资源库"
        actions={
          <>
            <div className="relative">
              <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              <input
                className="w-56 pl-9 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:border-blue-400"
                placeholder="搜索文件名..."
                value={searchInput}
                onChange={e => { setSearchInput(e.target.value); doSearch(e.target.value); }}
              />
            </div>
            <button
              onClick={() => { const cat = categories.find(c => c.id === activeCategoryId || c.children?.some(ch => ch.id === activeCategoryId)); setUploadTargetCatId(activeCategoryId === 'all' ? null : activeCategoryId as number); setUploadTargetCatName(activeCategoryId === 'all' ? '未分类' : (cat?.name || '')); setUploadOpen(true); }}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
              上传文件
            </button>
          </>
        }
      />

      {/* ── 三栏主体 ── */}
      <div className="flex flex-1 min-h-0 rounded-xl border border-border bg-white overflow-hidden shadow-sm">
        {/* 左栏：分类树 */}
        <div className="w-[220px] shrink-0 border-r border-border flex flex-col bg-accent/50">
          <div className="flex-1 overflow-y-auto px-3 py-3">
            {/* 全部 */}
            <div
              onClick={() => { setActiveCategoryId('all'); setPage(1); }}
              className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer mb-1 transition-colors ${
                activeCategoryId === 'all' ? 'bg-primary/10 text-primary border-l-2 border-blue-500' : 'hover:bg-accent text-foreground'
              }`}
            >
              <span className="flex items-center gap-2 text-sm"><FolderIcon className="w-5 h-5 inline-block" /> 全部</span>
              <span className="text-xs text-muted-foreground bg-accent rounded-full px-1.5">{total}</span>
            </div>
            {/* 分类 */}
            {categories.map(c => (
              <div key={c.id}>
                <div
                  className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer mb-0.5 transition-colors group ${
                    activeCategoryId === c.id ? 'bg-primary/10 text-primary border-l-2 border-blue-500' : 'hover:bg-accent text-foreground'
                  }`}
                  onClick={() => { setActiveCategoryId(c.id); setPage(1); }}
                  onContextMenu={e => { e.preventDefault(); setContextCat(c); setCtxMenuPos({ x: e.clientX, y: e.clientY }); }}
                >
                  <span className="flex items-center gap-2 text-sm"><FolderIcon className="w-5 h-5 inline-block" /> {c.name}</span>
                  <span className="text-xs text-muted-foreground bg-accent rounded-full px-1.5">{c.image_count}</span>
                </div>
                {/* 子分类 */}
                {c.children.map(ch => (
                  <div
                    key={ch.id}
                    className={`flex items-center justify-between pl-8 pr-3 py-1.5 rounded-lg cursor-pointer mb-0.5 transition-colors text-sm ${
                      activeCategoryId === ch.id ? 'bg-primary/10 text-primary' : 'hover:bg-accent text-foreground'
                    }`}
                    onClick={() => { setActiveCategoryId(ch.id); setPage(1); }}
                    onContextMenu={e => { e.preventDefault(); setContextCat(ch); setCtxMenuPos({ x: e.clientX, y: e.clientY }); }}
                  >
                    <span>{ch.name}</span>
                    <span className="text-xs text-muted-foreground bg-accent rounded-full px-1.5">{ch.image_count}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
          {/* 新建分类 */}
          <div className="border-t border-border px-3 py-2">
            {showNewCat ? (
              <div className="flex gap-1">
                <input
                  className="flex-1 text-sm border border-border rounded px-2 py-1 focus:outline-none focus:border-blue-400"
                  placeholder="分类名"
                  value={newCatName}
                  onChange={e => setNewCatName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') createCategory(); if (e.key === 'Escape') { setShowNewCat(false); setNewCatName(''); } }}
                  autoFocus
                />
              </div>
            ) : (
              <button onClick={() => setShowNewCat(true)} className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-1">
                <span>+</span> 新建分类
              </button>
            )}
          </div>
        </div>

        {/* 中栏：图片网格 */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* 工具栏 */}
          <div className="px-4 py-2 border-b border-border flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <button onClick={() => setViewMode('grid')} className={`px-2 py-1 rounded ${viewMode === 'grid' ? 'bg-accent text-foreground' : ''}`}>▦ 网格</button>
              <button onClick={() => setViewMode('list')} className={`px-2 py-1 rounded ${viewMode === 'list' ? 'bg-accent text-foreground' : ''}`}>☰ 列表</button>
            </div>
            <select value={sortBy} onChange={e => { setSortBy(e.target.value); setPage(1); }} className="border border-border rounded px-2 py-1 text-xs">
              <option value="newest">最新上传</option>
              <option value="name">文件名</option>
              <option value="size">文件大小</option>
            </select>
          </div>

          {/* 内容区 */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              /* 骨架屏 */
              <div className="grid grid-cols-4 gap-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="rounded-lg border border-border overflow-hidden">
                    <div className="skeleton-bar h-[120px] w-full rounded-none" />
                    <div className="p-2 space-y-2">
                      <div className="skeleton-bar h-3 w-3/4 rounded" />
                      <div className="skeleton-bar h-2 w-1/2 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : images.length === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <div className="text-5xl mb-3">📂</div>
                  <div className="text-sm mb-3">还没有文件资源</div>
                  <button onClick={() => { setUploadTargetCatId(activeCategoryId === 'all' ? null : activeCategoryId as number); setUploadTargetCatName(activeCategoryId === 'all' ? '未分类' : (categories.find(c => c.id === activeCategoryId || c.children?.some(ch => ch.id === activeCategoryId))?.name || '')); setUploadOpen(true); }} className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
                    <ArrowUpTrayIcon className="w-5 h-5 inline-block" /> 上传文件
                  </button>
                </div>
              </div>
            ) : viewMode === 'grid' ? (
              /* 网格 */
              <div className="grid grid-cols-4 gap-3">
                {images.map(img => {
                  const isSel = selectedImage?.id === img.id;
                  return (
                    <div
                      key={img.id}
                      onClick={() => selectImage(img)}
                      className={`rounded-lg border overflow-hidden cursor-pointer transition-all hover:shadow-lg group ${
                        isSel ? 'ring-2 ring-blue-500' : 'border-border'
                      }`}
                    >
                      <div className="relative bg-accent h-[120px] flex items-center justify-center overflow-hidden">
                        {isImageFile(img) ? (
                          <img src={getImageUrl(img)} alt={img.original_name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-4xl">{fileIcon(img)}</span>
                        )}
                        {/* hover遮罩 — 点击打开预览 */}
                        <div
                          className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100"
                          onClick={e => { e.stopPropagation(); if (isImageFile(img)) setFullscreen(img); }}
                        >
                          {isImageFile(img) && <span className="text-white text-xl">👁</span>}
                        </div>
                      </div>
                      <div className="p-2">
                        <div className="text-xs text-foreground truncate">{stripExt(img.original_name)}</div>
                        <div className="flex items-center gap-1 mt-1">
                          {img.is_analyzed && img.ai_tags ? (
                            <>
                              {(img.ai_tags as string[]).slice(0, 3).map(t => (
                                <span key={t} className="text-[10px] bg-primary/10 text-primary px-1 rounded">{t}</span>
                              ))}
                              {(img.ai_tags as string[]).length > 3 && (
                                <span className="text-[10px] text-muted-foreground">+{(img.ai_tags as string[]).length - 3}</span>
                              )}
                            </>
                          ) : null}
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          <span className={`w-1.5 h-1.5 rounded-full ${img.is_analyzed ? 'bg-green-400' : 'bg-accent'}`} />
                          <span className="text-[10px] text-muted-foreground">{img.is_analyzed ? `已分析 · ${formatDate(img.created_at)}` : '待分析'}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* 列表 */
              <div className="divide-y divide-border">
                {images.map(img => (
                  <div
                    key={img.id}
                    onClick={() => selectImage(img)}
                    className={`flex items-center gap-3 px-2 py-2 cursor-pointer hover:bg-accent rounded transition-colors ${
                      selectedImage?.id === img.id ? 'bg-primary/10 ring-1 ring-blue-200' : ''
                    }`}
                  >
                    {isImageFile(img) ? (
                      <img
                        src={getImageUrl(img)} alt=""
                        className="w-12 h-12 object-cover rounded bg-accent shrink-0 cursor-pointer hover:ring-2 hover:ring-blue-300"
                        onClick={e => { e.stopPropagation(); setFullscreen(img); }}
                      />
                    ) : (
                      <span
                        className="w-12 h-12 flex items-center justify-center rounded bg-accent shrink-0 text-2xl"
                      >
                        {fileIcon(img)}
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-foreground truncate">{stripExt(img.original_name)}</div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                        <span>{img.category_name}</span>
                        <span>{formatSize(img.file_size)}</span>
                        <span>·</span>
                        <span>{formatDate(img.created_at)}</span>
                      </div>
                    </div>
                    <span className={`w-1.5 h-1.5 rounded-full ${img.is_analyzed ? 'bg-green-400' : 'bg-accent'}`} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 分页 */}
          {totalPages > 1 && (
            <div className="px-4 py-2 border-t border-border flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <span>共 {total} 张</span>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="px-2 py-0.5 border rounded disabled:opacity-30">‹</button>
              {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                const pn = start + i;
                return pn <= totalPages ? (
                  <button key={pn} onClick={() => setPage(pn)} className={`px-2 py-0.5 rounded ${pn === page ? 'bg-blue-600 text-white' : 'hover:bg-accent'}`}>{pn}</button>
                ) : null;
              })}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-2 py-0.5 border rounded disabled:opacity-30">›</button>
            </div>
          )}
        </div>

        {/* 右栏：详情预览面板 */}
        <div className="w-[320px] shrink-0 overflow-y-auto border-l border-border bg-white">
          {detailImage ? (
            <div className="p-4 relative">
              {savedFlash && <div className="absolute top-2 right-3 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded">✓ 已保存</div>}
              {/* 文件预览 */}
              <div className="bg-accent rounded-xl flex items-center justify-center overflow-hidden mb-4 h-48">
                {isImageFile(detailImage) ? (
                  <img
                    src={getImageUrl(detailImage)} alt={detailImage.original_name}
                    className="max-w-full max-h-full object-contain cursor-pointer"
                    onClick={() => setFullscreen(detailImage)}
                  />
                ) : (
                  <div className="text-center">
                    <div className="text-5xl mb-2">{fileIcon(detailImage)}</div>
                    <div className="text-xs text-muted-foreground">{detailImage.mime_type}</div>
                    <a
                      href={getImageUrl(detailImage)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-2 px-3 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      onClick={e => e.stopPropagation()}
                    >
                      下载 / 查看
                    </a>
                  </div>
                )}
              </div>

              {/* 文件名 */}
              <div className="mb-3">
                <div className="text-[10px] text-muted-foreground mb-0.5">文件名</div>
                <input
                  className="w-full text-sm border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-400"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  onBlur={() => { if (editName !== detailImage.original_name) saveField('name', editName); }}
                />
              </div>

              {/* 信息 */}
              <div className="grid grid-cols-2 gap-2 mb-3 text-xs text-muted-foreground">
                <div><span className="text-muted-foreground">尺寸</span> {detailImage.width ? `${detailImage.width}×${detailImage.height}` : '未知'}</div>
                <div><span className="text-muted-foreground">大小</span> {formatSize(detailImage.file_size)}</div>
                <div className="col-span-2"><span className="text-muted-foreground">上传</span> {new Date(detailImage.created_at).toLocaleString('zh-CN')}</div>
              </div>

              {/* 分类 */}
              <div className="mb-3">
                <div className="text-[10px] text-muted-foreground mb-0.5">分类</div>
                <select
                  className="w-full text-sm border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-400"
                  value={editCatId}
                  onChange={e => { const v = Number(e.target.value); setEditCatId(v); saveField('cat', v); }}
                >
                  {flatCats(categories).map(c => (
                    <option key={c.id} value={c.id}>{c.level === 1 ? '  ' : ''}{c.name}</option>
                  ))}
                </select>
              </div>

              {/* AI 描述 */}
              <div className="mb-3">
                <div className="text-[10px] text-muted-foreground mb-0.5">AI 描述</div>
                <textarea
                  className="w-full text-sm border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-400 resize-y"
                  rows={4}
                  value={editDesc}
                  onChange={e => setEditDesc(e.target.value)}
                  onBlur={() => { if (editDesc !== (detailImage.ai_description || '')) saveField('desc', editDesc); }}
                />
              </div>

              {/* AI 标签 */}
              <div className="mb-4">
                <div className="text-[10px] text-muted-foreground mb-1">AI 标签</div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {editTags.map(t => (
                    <span key={t} className="inline-flex items-center gap-0.5 text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                      {t}
                      <button onClick={() => { const nt = editTags.filter(x => x !== t); setEditTags(nt); saveField('tags', nt); }} className="hover:text-red-500">✕</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-1">
                  <input
                    className="flex-1 text-xs border border-border rounded px-2 py-1 focus:outline-none focus:border-blue-400"
                    placeholder="添加标签"
                    value={newTag}
                    onChange={e => setNewTag(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && newTag.trim()) {
                        const nt = [...editTags, newTag.trim()];
                        setEditTags(nt);
                        setNewTag('');
                        saveField('tags', nt);
                      }
                    }}
                  />
                </div>
              </div>

              {/* 操作按钮 */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleAnalyze(detailImage.id)}
                  disabled={analyzing}
                  className={`flex-1 py-1.5 text-sm rounded-lg flex items-center justify-center gap-1.5 transition-all ${
                    analyzing
                      ? 'bg-primary/10 text-blue-400 cursor-wait border border-blue-100'
                      : 'border border-border text-foreground hover:bg-accent hover:text-foreground'
                  }`}
                >
                  {analyzing ? (
                    <>
                      <span className="inline-block w-3.5 h-3.5 border-2 border-blue-300 border-t-blue-500 rounded-full animate-spin" />
                      <span className="animate-pulse">分析中...</span>
                    </>
                  ) : (
                    <><ArrowPathIcon className="w-5 h-5 inline-block" /> 重新分析</>
                  )}
                </button>
                <button
                  onClick={() => confirmDeleteImage(detailImage.id)}
                  className="flex-1 py-1.5 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors flex items-center justify-center gap-1"
                >
                  <TrashIcon className="w-4 h-4" /> 删除
                </button>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              <ClipboardDocumentListIcon className="w-5 h-5 inline-block" /> 选择一个文件查看详情
            </div>
          )}
        </div>
      </div>

      {/* ── 上传模态框 ── */}
      {uploadOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => { if (!uploading) setUploadOpen(false); }}>
          <div className="bg-white rounded-2xl shadow-xl w-[520px] max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h3 className="font-semibold text-foreground">上传文件</h3>
              <button onClick={() => { if (!uploading) setUploadOpen(false); }} className="text-muted-foreground hover:text-foreground">✕</button>
            </div>
            <div className="p-5">
              {/* 目标分类提示 */}
              <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-primary/10 border border-blue-100 rounded-lg text-sm">
                <span className="text-primary"><FolderIcon className="w-5 h-5 inline-block" /></span>
                <span className="text-muted-foreground">上传到分类：</span>
                <span className="font-semibold text-primary">{uploadTargetCatName}</span>
                <span className="text-xs text-blue-400 ml-auto">
                  {uploadTargetCatName === '未分类' ? '可在上传后手动归类' : '切换左侧分类可更改目标'}
                </span>
              </div>

              {/* 拖拽区 */}
              <div
                className="border-2 border-dashed border-border rounded-xl p-10 text-center hover:border-blue-400 hover:bg-primary/5 transition-colors cursor-pointer mb-4"
                onDragOver={e => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => document.getElementById('upload-input')?.click()}
              >
                <div className="text-4xl mb-3"><ArrowUpTrayIcon className="w-5 h-5 inline-block" /></div>
                <div className="text-sm text-muted-foreground mb-1">拖拽图片到此处</div>
                <div className="text-xs text-muted-foreground">或点击选择文件</div>
                <div className="text-xs text-muted-foreground mt-2">支持 JPG / PNG / WebP / SVG / PDF / Word · 单文件 ≤ 20MB</div>
                <input id="upload-input" type="file" multiple accept=".jpg,.jpeg,.png,.webp,.svg,.pdf,.docx" className="hidden" onChange={handleUploadFiles} />
              </div>

              {/* 已选文件 */}
              {uploadFiles.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs text-muted-foreground mb-2">已选择 {uploadFiles.length} 个文件：</div>
                  <div className="border border-border rounded-lg divide-y divide-border max-h-40 overflow-y-auto">
                    {uploadFiles.map((f, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                        <div className="flex items-center gap-2 min-w-0">
                          <span><DocumentTextIcon className="w-5 h-5 inline-block" /></span>
                          <span className="truncate text-foreground">{f.name}</span>
                          <span className="text-xs text-muted-foreground shrink-0">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                        </div>
                        {!uploading && <button onClick={() => removeUploadFile(i)} className="text-muted-foreground hover:text-red-500 shrink-0">✕</button>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 上传中进度 */}
              {uploading && (
                <div className="mb-4 space-y-2">
                  {uploadFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <span><DocumentTextIcon className="w-5 h-5 inline-block" /> {f.name}</span>
                      <div className="flex-1 bg-accent rounded-full h-2">
                        <div className="bg-primary/100 h-2 rounded-full transition-all" style={{ width: `${uploadProgress[i] || 0}%` }} />
                      </div>
                      <span className="text-xs text-muted-foreground">{uploadProgress[i] || 0}%</span>
                    </div>
                  ))}
                </div>
              )}

              {/* 自动分析toggle */}
              <div className="flex items-center gap-2 mb-4 text-sm text-foreground">
                <span>上传后自动 AI 分析</span>
                <div className="w-9 h-5 bg-primary/100 rounded-full relative cursor-pointer">
                  <div className="w-4 h-4 bg-white rounded-full absolute top-0.5 right-0.5" />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button onClick={() => { if (!uploading) setUploadOpen(false); }} className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-accent">取消</button>
                <button onClick={doUpload} disabled={uploading || !uploadFiles.length} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {uploading ? '上传中...' : `开始上传 (${uploadFiles.length})`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── 全屏预览 ── */}
      {fullscreen && (
        <ImagePreviewModal
          image={fullscreen}
          images={images}
          onClose={() => setFullscreen(null)}
        />
      )}

      {/* ── 右键菜单 ── */}
      {contextCat && ctxMenuPos && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setContextCat(null); setCtxMenuPos(null); }} />
          <div className="fixed z-50 bg-white rounded-lg shadow-xl border border-border py-1 text-sm" style={{ left: ctxMenuPos.x, top: ctxMenuPos.y }}>
            {!contextCat.is_default && (
              <button
                className="block w-full text-left px-4 py-1.5 hover:bg-accent"
                onClick={() => { setNewSubCatParent(contextCat); setNewSubCatName(''); setContextCat(null); setCtxMenuPos(null); }}
              >
                📁 新建子分类
              </button>
            )}
            {!contextCat.is_default && (
              <button
                className="block w-full text-left px-4 py-1.5 hover:bg-accent"
                onClick={() => { setRenameCat(contextCat); setRenameName(contextCat.name); setContextCat(null); setCtxMenuPos(null); }}
              >
                ✏ 重命名
              </button>
            )}
            {!contextCat.is_default && (
              <button
                className="block w-full text-left px-4 py-1.5 hover:bg-accent text-red-500"
                onClick={() => { setConfirmDeleteCat(contextCat); setContextCat(null); setCtxMenuPos(null); }}
              >
                🗑 删除
              </button>
            )}
          </div>
        </>
      )}

      {/* ── 重命名弹窗 ── */}
      {renameCat && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl p-5 w-80">
            <h3 className="font-semibold mb-3">重命名分类</h3>
            <input className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400" value={renameName} onChange={e => setRenameName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') doRenameCat(); if (e.key === 'Escape') setRenameCat(null); }} autoFocus />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setRenameCat(null)} className="px-3 py-1.5 text-sm border rounded-lg">取消</button>
              <button onClick={doRenameCat} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg">确认</button>
            </div>
          </div>
        </div>
      )}

      {/* ── 新建子分类弹窗 ── */}
      {newSubCatParent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl p-5 w-80">
            <h3 className="font-semibold mb-3">
              新建子分类
              <span className="text-sm font-normal text-muted-foreground ml-2">父级：{newSubCatParent.name}</span>
            </h3>
            <input
              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
              placeholder="子分类名称"
              value={newSubCatName}
              onChange={e => setNewSubCatName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') createSubCategory(); if (e.key === 'Escape') { setNewSubCatParent(null); setNewSubCatName(''); } }}
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => { setNewSubCatParent(null); setNewSubCatName(''); }} className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-accent">取消</button>
              <button onClick={createSubCategory} disabled={!newSubCatName.trim()} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">创建</button>
            </div>
          </div>
        </div>
      )}

      {/* ── 删除分类确认弹窗 ── */}
      {confirmDeleteCat && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl p-5 w-96">
            <h3 className="font-semibold mb-2">确认删除</h3>
            <p className="text-sm text-foreground mb-4">"{confirmDeleteCat.name}" 下有 {confirmDeleteCat.image_count} 张图片，将移至「未分类」。确认删除？</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDeleteCat(null)} className="px-3 py-1.5 text-sm border rounded-lg">取消</button>
              <button onClick={() => doDeleteCat(confirmDeleteCat)} className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg">确认删除</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaLibrary;
